// Copyright © 2019-2026 Focal Systems. All rights reserved.

import { useTranslation } from "react-i18next";
import type { EtlProcessingDetail } from "@/api/hooks/use-imports";
import { ChainLink } from "@/components/shared/chain-link";
import { DateTimeValue } from "@/components/shared/datetime-value";
import { ImportTypeBadge } from "@/components/shared/import-type-badge";
import { StoreLink } from "@/components/shared/store-link";
import { Separator } from "@/components/ui/separator";
import { Switch } from "@/components/ui/switch";

interface TransformationInfoTabProps {
  etl: EtlProcessingDetail;
}

export function TransformationInfoTab({ etl }: TransformationInfoTabProps) {
  const { t } = useTranslation("back-office");

  return (
    <div className="max-w-2xl space-y-6 p-6">
      <div className="space-y-4">
        <div className="grid grid-cols-[160px_1fr] gap-y-3 text-sm">
          <span className="text-muted-foreground">{t("transformations.detail.fields.description")}</span>
          <span>{etl.description}</span>

          <span className="text-muted-foreground">{t("transformations.detail.fields.target")}</span>
          <span>
            {etl.storeName ? (
              <StoreLink storeName={etl.storeName} legibleName={etl.storeLegibleName} />
            ) : etl.chainName ? (
              <ChainLink chainName={etl.chainName} legibleName={etl.chainLegibleName} />
            ) : (
              "—"
            )}
          </span>

          <span className="text-muted-foreground">{t("transformations.detail.fields.outputTypes")}</span>
          <div className="flex flex-wrap gap-1">
            {etl.outputImportTypes.map((type) => (
              <ImportTypeBadge key={type} type={type} />
            ))}
          </div>

          <span className="text-muted-foreground">{t("transformations.detail.fields.perStore")}</span>
          <Switch checked={etl.isAvailablePerStore} className="pointer-events-none" />

          <span className="text-muted-foreground">{t("transformations.detail.fields.onlyLiveStores")}</span>
          <Switch checked={etl.isOnlyLiveStores} className="pointer-events-none" />

          <Separator className="col-span-2" />

          <span className="text-muted-foreground">{t("transformations.detail.fields.createdAt")}</span>
          <DateTimeValue value={etl.createdAt} />

          <span className="text-muted-foreground">{t("transformations.detail.fields.updatedAt")}</span>
          <DateTimeValue value={etl.updatedAt} />
        </div>
      </div>
    </div>
  );
}
